// ═══════════════════════════════════════════════════════════════════════════════
// CART OBSERVER — universal cart hook + anti-tamper guard
// ═══════════════════════════════════════════════════════════════════════════════
//
// 1. OBSERVE — Wraps window.fetch and XMLHttpRequest so any request to
//    /cart/(add|change|update|clear) — from theme code, a third-party app, the
//    Buy Button SDK, or a raw console fetch — fires a `cart:changed` DOM event
//    on success. See docs/cart-observer.md for the full rationale.
//
// 2. GUARD — The theme's own cart writes go through ajax-cart's apiLayer, which
//    brackets each one as "trusted" via RasasiCartObserver.beginTrusted/
//    endTrusted. ANY cart mutation that is NOT trusted (DevTools console, raw
//    fetch/XHR, pasted scripts) is treated as tampering and the cart is wiped
//    immediately — no checks, no confirmation. Opening DevTools is also deterred
//    (blocked shortcuts / right-click) and, when detected, wipes the cart too.
//
//    ⚠ HONEST LIMITATIONS — read before relying on this:
//    Client-side code can NEVER truly block DevTools or the console; everything
//    in the GUARD section is a deterrent a determined user can bypass (disable
//    JS, use a proxy, restore window.fetch, etc.). Shopify also recomputes every
//    price and line server-side at checkout, so console cart edits cannot change
//    prices or cheat the store — this guard just resets a tampered cart and
//    raises the effort bar. Toggle any of it off in CONFIG below if it ever
//    interferes with a real shopper.
//
// MUST load BEFORE ajax-cart.js and must NOT be deferred — the wrappers and the
// trusted-write signal have to be in place before any other script touches
// window.fetch or makes a cart request.
// ═══════════════════════════════════════════════════════════════════════════════

(function () {
  if (window.__cartObserverInstalled) return;
  window.__cartObserverInstalled = true;

  // ── Config — the single, simple switch surface ─────────────────────────────
  // Edit these here (kept out of the global object on purpose, so they can't be
  // flipped from the console at runtime).
  var CONFIG = {
    enabled: false, //             master switch for the whole anti-tamper GUARD — disabled for now
    clearOnUntrustedCart: true, // wipe the cart on any non-theme cart mutation
    blockShortcuts: true, //       swallow F12 / Ctrl+Shift+I|J|C / Ctrl+U
    blockContextMenu: true, //     swallow right-click (hides the "Inspect" entry)
    detectDevTools: true, //       poll for an open DevTools panel
    clearOnDevTools: true, //      wipe the cart when DevTools is detected open
    antiDebug: false //            debugger-trap loop — freezes the tab while
    //                             DevTools is open. Most aggressive + most
    //                             user-hostile (also blocks your own QA). Off.
  };

  // Never fight the merchant: the GUARD stays off inside Shopify's theme editor
  // and any preview/embed iframe, so editing and QA keep working normally.
  var inEditor =
    (window.Shopify && window.Shopify.designMode) || window.top !== window.self;
  var guardOn = CONFIG.enabled && !inEditor;

  // Matches /cart/add, /cart/add.js, /en/cart/change.js, /cart/update?…, /cart/clear.js
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

  var originalFetch = window.fetch.bind(window);

  // ── Trusted-write signal (shared with ajax-cart.js) ────────────────────────
  // ajax-cart's apiLayer wraps every one of its cart writes with beginTrusted/
  // endTrusted, so at emit time a theme write has depth > 0 while a console
  // write has depth 0. `hardClearing` guards our own remediation clear below so
  // it never recurses or re-triggers the wipe.
  var trustedDepth = 0;
  var hardClearing = false;

  window.RasasiCartObserver = {
    beginTrusted: function () {
      trustedDepth++;
    },
    endTrusted: function () {
      trustedDepth = trustedDepth > 0 ? trustedDepth - 1 : 0;
    },
    isTrusted: function () {
      return trustedDepth > 0;
    }
  };

  // ── Wrap fetch ─────────────────────────────────────────────────────────────
  window.fetch = async function (input, init) {
    var url =
      typeof input === 'string'
        ? input
        : input instanceof Request
          ? input.url
          : (input && input.url) || '';

    var isCartMutation = CART_MUTATION.test(url);
    var response;
    try {
      response = await originalFetch(input, init);
    } catch (err) {
      if (isCartMutation) emit('cart:error', { url: url, error: err });
      throw err;
    }

    if (isCartMutation && response.ok) {
      // Clone so the caller can still read the body.
      handleCartMutation(detectEndpoint(url), url, { response: response.clone() });
    }
    return response;
  };

  // ── Wrap XMLHttpRequest ────────────────────────────────────────────────────
  var OrigOpen = XMLHttpRequest.prototype.open;
  var OrigSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__cartUrl = String(url || '');
    return OrigOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    var xhr = this;
    if (CART_MUTATION.test(xhr.__cartUrl || '')) {
      xhr.addEventListener('load', function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          handleCartMutation(detectEndpoint(xhr.__cartUrl), xhr.__cartUrl, { xhr: xhr });
        }
      });
      xhr.addEventListener('error', function () {
        emit('cart:error', { url: xhr.__cartUrl });
      });
    }
    return OrigSend.apply(xhr, arguments);
  };

  // ── Core: every successful cart mutation flows through here ─────────────────
  function handleCartMutation(endpoint, url, extra) {
    // Preserve the universal-hook contract — ajax-cart listens for this.
    emit('cart:changed', Object.assign({ url: url, endpoint: endpoint }, extra));

    // GUARD: a mutation that did NOT come through the theme's trusted path is
    // console / DevTools / injected-script tampering → wipe the cart, no checks.
    // `hardClearing` skips the wipe our own clear request triggers.
    if (guardOn && CONFIG.clearOnUntrustedCart && !hardClearing && trustedDepth === 0) {
      hardClearCart('untrusted-cart-' + endpoint);
    }
  }

  // ── Wipe the cart, immediately and unconditionally ─────────────────────────
  // Goes through the wrapped fetch on purpose: the resulting `cart:changed`
  // event lets ajax-cart refresh the header / drawer to the now-empty state.
  // `hardClearing` is true for the whole call, so that nested event is ignored
  // by the GUARD above (no recursion).
  async function hardClearCart(reason) {
    if (hardClearing) return;
    hardClearing = true;
    try {
      emit('cart:tamper', { reason: reason });
      await window.fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}'
      });
    } catch (e) {
      // Best-effort — nothing useful to do if the clear itself fails.
    } finally {
      hardClearing = false;
    }
  }

  // ── DevTools deterrents (best-effort; trivially bypassable) ─────────────────
  if (guardOn && CONFIG.blockShortcuts) {
    window.addEventListener(
      'keydown',
      function (e) {
        var key = (e.key || '').toLowerCase();
        var blocked =
          e.keyCode === 123 || //                                          F12
          ((e.ctrlKey || e.metaKey) && e.shiftKey && (key === 'i' || key === 'j' || key === 'c')) || // inspect / console / picker
          ((e.ctrlKey || e.metaKey) && key === 'u'); //                    view source
        if (blocked) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
  }

  if (guardOn && CONFIG.blockContextMenu) {
    window.addEventListener(
      'contextmenu',
      function (e) {
        e.preventDefault();
      },
      true
    );
  }

  if (guardOn && CONFIG.antiDebug) {
    setInterval(function () {
      (function () {
        debugger;
      })();
    }, 1000);
  }

  if (guardOn && CONFIG.detectDevTools) {
    // Heuristic: docked DevTools shrinks the viewport well below the window.
    // Conservative threshold + a two-strike confirm avoids wiping a real
    // shopper's cart over a transient layout/zoom blip.
    var DEVTOOLS_GAP = 200;
    var devtoolsOpen = false;
    var pendingStrikes = 0;

    function devtoolsLooksOpen() {
      var widthGap = window.outerWidth - window.innerWidth > DEVTOOLS_GAP;
      var heightGap = window.outerHeight - window.innerHeight > DEVTOOLS_GAP;
      return widthGap || heightGap;
    }

    function pollDevTools() {
      if (devtoolsLooksOpen()) {
        pendingStrikes++;
        if (pendingStrikes >= 2 && !devtoolsOpen) {
          devtoolsOpen = true;
          emit('cart:tamper', { reason: 'devtools-open' });
          if (CONFIG.clearOnDevTools) hardClearCart('devtools-open');
        }
      } else {
        pendingStrikes = 0;
        devtoolsOpen = false; // re-arm so reopening triggers the wipe again
      }
    }

    window.addEventListener('resize', pollDevTools);
    setInterval(pollDevTools, 1000);
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  function detectEndpoint(url) {
    var m = url.match(/\/cart\/(add|change|update|clear)/i);
    return m ? m[1].toLowerCase() : 'unknown';
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
  }
})();
